/**
 * Shared 401 / 403 response handling.
 *
 * The axios interceptor in `frontend/src/api/client.js` is the single
 * consumer: it detects permission-denied 403s and surfaces a toast, and
 * on 401 it clears the local session and redirects to the Common Auth
 * login URL.
 *
 * Historical context (pre-Modularity Epic 6.4): a parallel `fetchWithAuth`
 * wrapper at `frontend/src/utils/fetchWrapper.js` also delegated here.
 * That wrapper has been retired alongside `AthenaBackendService` (its
 * only consumer); `api/client.js` is now the only HTTP path in the app.
 * Externally-observable behaviour is preserved: same toast text, same
 * redirect URL format, same storage keys cleared.
 *
 * The `handle403(detail, fallbackData)` signature accepts the response
 * body (`fallbackData`) so the message can fall back to server-provided
 * `data.message` / `data.detail` when the 403 is NOT a permission error
 * — that preserves the axios path's pre-consolidation behaviour
 * (otherwise this refactor would silently regress its toast quality).
 */

import { config } from '../config/env';
import { notify } from '../services/notificationBus';

const PERMISSION_KEYWORDS = [
  'Missing required permissions',
  'permission',
  'User has no assigned role',
  'User not found in workspace',
];

/**
 * Returns true when the 403 detail string matches one of the permission
 * keywords the backend uses for "you lack permission for this action."
 */
export function isPermissionDenied(detail) {
  if (typeof detail !== 'string') return false;
  return PERMISSION_KEYWORDS.some((kw) => detail.includes(kw));
}

/**
 * Build the toast / error message string for a 403.
 *
 * - If `isPermission` is true, return the canonical "You don't have
 *   permission to perform this action" string (byte-identical between
 *   both consumers pre-consolidation).
 * - Otherwise, prefer server-provided detail when `fallbackData`
 *   carries one (`data.message` then `data.detail`), falling back to
 *   the literal `'Access denied'` if nothing better is available.
 *
 * Pre-consolidation, the axios path used the server-detail fallback
 * and the fetch path hardcoded `'Access denied'`. We unify on the
 * richer behaviour for both.
 */
export function buildPermissionDeniedMessage(isPermission, fallbackData = null) {
  if (isPermission) return "You don't have permission to perform this action";
  if (fallbackData && typeof fallbackData === 'object') {
    const fromMessage =
      typeof fallbackData.message === 'string' ? fallbackData.message : null;
    const fromDetail =
      typeof fallbackData.detail === 'string' ? fallbackData.detail : null;
    return fromMessage || fromDetail || 'Access denied';
  }
  return 'Access denied';
}

/**
 * Run the full 403 side-effects (toast) and return `{isPermission, message}`
 * so the caller can shape its rejection envelope appropriately.
 *
 * `fallbackData` is the parsed response body (or any object with
 * `message` / `detail` keys); pass it to preserve server-provided
 * detail messages on non-permission 403s.
 */
export function handle403(detail, fallbackData = null) {
  const isPermission = isPermissionDenied(detail);
  const message = buildPermissionDeniedMessage(isPermission, fallbackData);
  notify({ type: 'error', message });
  return { isPermission, message };
}

/**
 * Run the full 401 side-effects: clear the local session, then navigate to
 * the Common Auth login URL with the current path stashed as the post-login
 * redirect target.
 *
 * NOTE: this is the *session-expiry* path, NOT a logout. It deliberately does
 * NOT set `logout_in_progress` — that flag means "the user explicitly logged
 * out" and is owned solely by `logout()`. Setting it here used to leak across
 * the round-trip to Common Auth and back, so the SSO return was misread as a
 * logout and bounced the user to the dashboard instead of their page.
 */
export function redirectToLogin() {
  localStorage.removeItem('athena_session');
  localStorage.removeItem('user_data');

  const currentPath = window.location.pathname + window.location.search;
  // product=athena lets Common Auth route the user back through the SSO handoff
  // to Athena after re-login (deep-link return); redirect is the path to return to.
  const redirectUrl = `${config.COMMON_AUTH_LOGIN_URL}?product=athena&redirect=${encodeURIComponent(currentPath)}`;
  window.location.href = redirectUrl;
}
